import { Crown, Shield, Briefcase, Code, Server, Users, Target, Lightbulb } from "lucide-react";

export const ItStakeholderRolesSection = () => {
  const roles = [
    {
      icon: Crown,
      title: "Chief Technology Officer (CTO)",
      subtitle: "Technology Strategy & Innovation",
      responsibilities: [
        "Define long-term technology vision and roadmap",
        "Evaluate and adopt emerging technologies",
        "Lead R&D and product development",
        "Represent tech in board and investor meetings",
        "Build and mentor engineering leadership",
      ],
      reports: "CEO / Board",
      focus: "Future-focused: What should we build?",
      color: "text-primary",
      bgColor: "bg-primary/10",
    },
    {
      icon: Shield,
      title: "Chief Information Officer (CIO)",
      subtitle: "IT Operations & Governance",
      responsibilities: [
        "Manage IT infrastructure and operations",
        "Ensure cybersecurity and compliance",
        "Optimize IT spending and vendor relationships",
        "Align IT with business objectives",
        "Oversee data governance and privacy",
      ],
      reports: "CEO / CFO",
      focus: "Present-focused: How do we run efficiently?",
      color: "text-chart-2",
      bgColor: "bg-chart-2/10",
    },
    {
      icon: Briefcase,
      title: "IT Director / VP of IT",
      subtitle: "Execution & Team Leadership",
      responsibilities: [
        "Manage IT teams and daily operations",
        "Execute technology projects and initiatives",
        "Implement policies set by CIO/CTO",
        "Budget management and resource allocation",
        "Vendor management and procurement",
      ],
      reports: "CIO / CTO",
      focus: "Execution-focused: How do we deliver?",
      color: "text-chart-4",
      bgColor: "bg-chart-4/10",
    },
    {
      icon: Server,
      title: "DevOps Engineer / SRE",
      subtitle: "Deployment & Reliability",
      responsibilities: [
        "Build and maintain CI/CD pipelines",
        "Manage cloud infrastructure (AWS, GCP, Azure)",
        "Monitor system performance and uptime",
        "Automate deployments and scaling",
        "Implement security best practices",
      ],
      reports: "IT Director / Engineering Manager",
      focus: "Delivery-focused: How do we ship reliably?",
      color: "text-chart-1",
      bgColor: "bg-chart-1/10",
    },
    {
      icon: Code,
      title: "Engineering Manager / Tech Lead",
      subtitle: "Development & Quality",
      responsibilities: [
        "Lead development teams and sprints",
        "Code review and technical decisions",
        "Mentor developers and grow talent",
        "Translate requirements into technical specs",
        "Ensure code quality and testing",
      ],
      reports: "CTO / VP of Engineering",
      focus: "Quality-focused: How do we build it right?",
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
    },
    {
      icon: Target,
      title: "Product Manager",
      subtitle: "Requirements & Priorities",
      responsibilities: [
        "Define product vision and roadmap",
        "Prioritize features based on user value",
        "Gather and document requirements",
        "Coordinate between business and engineering",
        "Measure product success and iterate",
      ],
      reports: "VP of Product / CEO",
      focus: "User-focused: What should we build next?",
      color: "text-orange-500",
      bgColor: "bg-orange-500/10",
    },
  ];

  return (
    <section className="py-8 sm:py-12">
      <h2 className="text-2xl sm:text-3xl font-display font-bold text-foreground mb-6">
        Key IT Stakeholder Roles Explained
      </h2>
      <p className="text-muted-foreground mb-8 max-w-3xl">
        Each role has distinct responsibilities. Understanding these boundaries prevents overlap 
        and ensures accountability in technology projects.
      </p>

      <div className="grid gap-6">
        {roles.map((role, index) => (
          <div
            key={index}
            className="bg-card rounded-xl border border-border p-6 hover:border-primary/30 transition-colors"
          >
            <div className="flex flex-col lg:flex-row lg:items-start gap-5">
              <div className={`w-14 h-14 rounded-xl ${role.bgColor} flex items-center justify-center shrink-0`}>
                <role.icon className={`w-7 h-7 ${role.color}`} />
              </div>
              <div className="flex-1">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                  <div>
                    <h3 className="font-semibold text-lg text-foreground">{role.title}</h3>
                    <p className={`text-sm ${role.color} font-medium`}>{role.subtitle}</p>
                  </div>
                  <div className="text-xs text-muted-foreground bg-muted/50 px-3 py-1.5 rounded-full">
                    Reports to: {role.reports}
                  </div>
                </div>
                
                <div className="mb-4">
                  <p className="text-sm font-medium text-foreground mb-2">Key Responsibilities:</p>
                  <ul className="grid sm:grid-cols-2 gap-1.5">
                    {role.responsibilities.map((resp, idx) => (
                      <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full ${role.bgColor} mt-1.5 shrink-0`} />
                        {resp}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${role.bgColor} text-sm`}>
                  <Lightbulb className={`w-4 h-4 ${role.color}`} />
                  <span className={role.color}>{role.focus}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
